import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import PatientProfileTab from '../../components/patientTabs/PatientProfileTab';
import MedicalHistory from '../../components/patientTabs/MedicalHistory';
import Dependencies from '../../components/patientTabs/Dependencies';
import HealthRecordTab from '../../components/patientTabs/HealthRecordTab';
import CustomPrice from '../../components/patientTabs/CustomPrice';
import PatientPasswordTab from '../../components/patientTabs/PatientPasswordTab';
import ProgrammeCard from '../../components/programmes/ProgrammeCard';
import { getPatientById } from '../../redux/patientSlice';
import { getDoctorById, clearSelectedDoctor } from '../../redux/doctorSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import CommonLoader from '../../components/CommonLoader';
import ErrorPage from '../../components/ErrorPage';
import { tryingnavigate, setPendingNavigation, clearPendingNavigation } from '../../redux/blockingSlice';
import { ArrowLeftCircle, UserX } from 'lucide-react';
import { extractErrorMessage } from '../../utils/extractErrorMessage';
import { hospitalPatientCode } from '../../utils/patientCode';
import usePlanControl from '../../hooks/usePlanControl';
import { getCustomPricesByPatientAndDoctor, getCustomPricesByPatientId } from '../../redux/customPriceSlice';
import { toast } from 'react-toastify';

function PatientProfile() {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { resnavigate, pendingNavigation } = useSelector((state) => state.blocking);
    const { t, i18n } = useTranslation(['patientprofilepage']);

    const { selectedPatient, isLoading, error } = useSelector((state) => state.patient);


    const userRole = localStorage.getItem('Role');
    const patientId = userRole === 'patient' ? localStorage.getItem('ID') : location?.state?.patientId;
    const doctorId = userRole === 'doctor' ? localStorage.getItem('ID') : '';

    const [activeTab, setActiveTab] = useState('Profile');
    const [mobileTabOpen, setMobileTabOpen] = useState(false);
    const isInitialCall = useRef(true);

    // Tabs setup
    const allTabs = [
        { key: 'Profile', label: t('patientprofilepage.tabs.profile'), testId: 'patient-profile-tab-profile' },
        { key: 'Medical History', label: t('patientprofilepage.tabs.medical_history'), testId: 'patient-profile-tab-medical-history' },
        // THE PERSON'S OWN RECORD (Phase 3), not this hospital's notes about
        // them: seven sections kept once and read wherever they are treated.
        // Who may open it is the server's answer — a doctor who has treated
        // them, or an isolated clinic's admin — so the tab is offered to
        // everyone and the panel explains a refusal in words.
        { key: 'Health Record', label: t('patientprofilepage.tabs.health_record'), testId: 'patient-profile-tab-health-record' },
        { key: 'Dependencies', label: t('patientprofilepage.tabs.dependencies'), testId: 'patient-profile-tab-dependencies' },
        { key: 'Custom Price', label: t('patientprofilepage.tabs.custom_price'), role: 'adminOnly', testId: 'patient-profile-tab-custom-price' },
        // Files tab retired (owner, 2026-09-02) — the toolbox API behind it
        // was deleted with the UI.
        { key: 'Password', label: t('patientprofilepage.tabs.password'), role: 'patientOnly', testId: 'patient-profile-tab-password' },
    ];

    // 1 Oct 2026 (owner): two tabs follow the DOCTOR's plan, which the server
    // now enforces — the cross-hospital Health Record needs visit_sharing,
    // Custom Price needs custom_pricing. usePlanControl answers true for
    // anyone who is not a doctor, so the admin keeps both.
    const visitSharingOn = usePlanControl('visit_sharing');
    const customPricingOn = usePlanControl('custom_pricing');
    const visibleTabs = allTabs.filter((tab) => {
        if (tab.key === 'Health Record' && !visitSharingOn) return false;
        if (tab.key === 'Custom Price' && !customPricingOn) return false;
        if (tab.role === 'adminOnly') {
            return userRole !== 'patient';
        }
        if (tab.role === 'patientOnly') {
            return userRole === 'patient';
        }
        return true;
    });

    // Check if profile is complete
    const profileComplete = selectedPatient?.profileStatus === 'complete';

    // Define tab locks - disable Medical History, Dependencies, and Files if profile is incomplete
    const tabLocks = {
        'Medical History': !profileComplete,
        'Dependencies': !profileComplete,
    };

    const handleTabClick = (e, key) => {
        if (resnavigate) {
            e.preventDefault();
            dispatch(setPendingNavigation({ kind: 'tab', value: key }));
            dispatch(tryingnavigate(true));
            return;
        }

        if (isLoading) return;

        // Check if tab is locked
        if (tabLocks[key]) {
            toast.warn(t('patientprofilepage.warning_profile') || 'Complete your profile and access this tab');
            return;
        }

        setActiveTab(key);
    };

    // Auto-apply the requested tab once the dirty tab releases the block.
    // Respect tabLocks so an incomplete profile can't be sidestepped via discard.
    useEffect(() => {
        if (!resnavigate && pendingNavigation?.kind === 'tab' && pendingNavigation.value) {
            const targetTab = pendingNavigation.value;
            if (!tabLocks[targetTab]) {
                setActiveTab(targetTab);
            }
            dispatch(clearPendingNavigation());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resnavigate, pendingNavigation, dispatch]);

    useEffect(() => {
        if (patientId) {
            if (isInitialCall.current) {
                dispatch(getPatientById(patientId));
                if (userRole === 'doctor') {
                    console.log('patientId,doctorId', patientId, doctorId)
                    dispatch(getDoctorById(doctorId));
                    dispatch(getCustomPricesByPatientAndDoctor({ patientId, doctorId }));
                } else {
                    // The Custom Price tab's admin picker loads the doctor it
                    // prices for. A doctor left in the store by another screen
                    // must not seed that form, so the slot starts empty here.
                    dispatch(clearSelectedDoctor());
                    dispatch(getCustomPricesByPatientId({ patientId }));
                }
                isInitialCall.current = false;
            }
        }
    }, [dispatch, patientId, userRole]);

    // Close mobile dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (mobileTabOpen && !event.target.closest('[data-testid~="patient-profile-mobile-tabs-dropdown"]')) {
                setMobileTabOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [mobileTabOpen]);

    // NOT ONE OF YOUR PATIENTS (owner, 2 Oct 2026). Where All Patient Mode is
    // OFF a doctor opens only their own patients, and the server answers 403
    // NOT_YOUR_PATIENT for anyone else. Say that in words, with the way back
    // to the doctor's own list — not the generic "try again" page, which a
    // reload cannot fix.
    if (error?.code === 'NOT_YOUR_PATIENT') {
        return (
            <div data-testid="patient-profile-not-your-patient" className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100 mx-auto flex flex-col items-center text-center gap-3 py-12">
                <div className="h-14 w-14 rounded-full bg-[#4ABDBD]/10 flex items-center justify-center">
                    <UserX className="h-7 w-7 text-[#2D8A8A]" aria-hidden="true" />
                </div>
                <h2 className="text-lg font-semibold text-gray-800">
                    {t('patientprofilepage.not_your_patient_title', 'Not one of your patients')}
                </h2>
                <p data-testid="patient-profile-not-your-patient-message" className="text-sm text-gray-600 max-w-md">
                    {extractErrorMessage(error.message, t('patientprofilepage.not_your_patient_message', 'This patient is not one of yours.'), i18n?.language || 'en')}
                </p>
                <p className="text-xs text-gray-500 max-w-md">
                    {t('patientprofilepage.not_your_patient_hint', 'You can open the patients you treat, or add a new one.')}
                </p>
                <button
                    type="button"
                    data-testid="patient-profile-not-your-patient-back"
                    onClick={() => navigate(userRole === 'doctor' ? '/myPatient' : '/patient')}
                    className="mt-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white px-4 py-2 rounded-xl hover:from-[#3AA8A8] hover:to-[#2D8A8A] font-semibold transition-all duration-300 shadow"
                >
                    {t('patientprofilepage.back_to_patients', 'Back to Patients')}
                </button>
            </div>
        );
    }

    // Handle error state
    if (error) {
        if (error?.status >= 500 && error?.status <= 599) {
            return <ErrorPage error={error} pageName={t('patientprofilepage.heading')} />;
        }
        if (error?.message === 'Network Error') {
            return <ErrorPage error={error} pageName={t('patientprofilepage.heading')} />;
        }
        return <ErrorPage error={error} pageName={t('patientprofilepage.heading')} />;
    }

    return (
        <div data-testid="patient-profile-container" className="bg-white rounded-2xl p-4 shadow-xl border border-gray-100 sm:p-6 mx-auto">
            {/* Back Button */}
            <div data-testid="patient-profile-header" className="mb-4 flex gap-2">
                {userRole !== 'patient' && (
                    <button
                        data-testid="patient-profile-back-button"
                        onClick={() => navigate(userRole === 'doctor' ? '/myPatient' : '/patient', { state: { returnTab: location.state?.returnTab } })}
                        disabled={isLoading}
                        className={`bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white p-2 rounded-xl hover:from-[#3AA8A8] hover:to-[#2D8A8A] font-semibold transition-all duration-300 cursor-pointer shadow-lg hover:shadow-xl hover:scale-105 ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        <ArrowLeftCircle data-testid="back-arrow-icon" className="h-5 w-5" />
                    </button>
                )}
                {userRole !== 'patient' && (
                    <h1 data-testid="patient-profile-heading" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent ml-2">
                        {t('patientprofilepage.heading')}
                    </h1>
                )}
                {/* THIS HOSPITAL'S NUMBER for the person (APO034) — the id the
                    desk quotes out loud. It belongs to the allocation, not to
                    the person: the same human carries a different one at every
                    hospital they visit, which is why the tooltip names the
                    scope rather than calling it "the patient id". */}
                {userRole !== 'patient' && hospitalPatientCode(selectedPatient) && (
                    <span
                        data-testid="patient-profile-code"
                        title={t('patientprofilepage.patient_number_here', 'Patient number at this hospital')}
                        className="self-center text-xs font-mono font-semibold text-[#2D8A8A] bg-[#4ABDBD]/10 border border-[#4ABDBD]/30 rounded-lg px-2 py-1 ml-1"
                    >
                        {hospitalPatientCode(selectedPatient)}
                    </span>
                )}
                {userRole === 'patient' && (
                    <h1 data-testid="patient-profile-heading-my-profile" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent ml-2">
                        {t('patientprofilepage.heading1')}
                    </h1>
                )}
            </div>

            {/* Modern Navigation Tabs */}
            <div data-testid="patient-profile-tabs-container" className="mb-6">
                {/* Desktop Navigation Tabs */}
                <div data-testid="patient-profile-tabs" className="hidden w-fit sm:flex flex-wrap justify-start gap-2 bg-gradient-to-r from-gray-50 to-blue-50/30 p-1 rounded-2xl shadow border border-gray-200">
                    {visibleTabs.map(({ key, label, testId }) => (
                        <button
                            key={key}
                            data-testid={testId}
                            onClick={(e) => handleTabClick(e, key)}
                            disabled={isLoading}
                            className={`relative px-6 py-3 text-sm font-semibold transition-colors duration-300 rounded-xl z-10 ${activeTab === key && !isLoading && !tabLocks[key]
                                ? 'text-white shadow-md'
                                : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'
                                } ${(isLoading || tabLocks[key]) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                            {activeTab === key && !isLoading && !tabLocks[key] && (
                                <motion.div
                                    layoutId="activePatientTab"
                                    className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                            <span className="relative z-20">{label}</span>
                        </button>
                    ))}
                </div>

                {/* Mobile Navigation - Dropdown Style */}
                <div data-testid="patient-profile-mobile-tabs-dropdown" className="sm:hidden">
                    <div className="bg-gradient-to-r from-gray-50 to-blue-50/30 rounded-2xl shadow-lg border border-gray-200 p-2">
                        <div className="relative">
                            <button
                                data-testid="mobile-tab-selector"
                                className="w-full flex items-center justify-between px-4 py-3 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 text-gray-700 font-semibold cursor-pointer"
                                onClick={() => setMobileTabOpen(!mobileTabOpen)}
                            >
                                <span>{visibleTabs.find(tab => tab.key === activeTab)?.label}</span>
                                <svg
                                    className={`w-5 h-5 transition-transform duration-300 ${mobileTabOpen ? 'rotate-180' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            {mobileTabOpen && (
                                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
                                    {visibleTabs.map(({ key, label, testId }) => (
                                        <button
                                            key={key}
                                            data-testid={`mobile-${testId}`}
                                            onClick={(e) => {
                                                handleTabClick(e, key);
                                                setMobileTabOpen(false);
                                            }}
                                            className={`w-full text-left px-4 py-3 text-sm font-semibold transition-all duration-300 border-b border-gray-100 last:border-b-0
                                                ${activeTab === key && !isLoading && !tabLocks[key]
                                                    ? 'text-white bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A]'
                                                    : 'text-gray-600 hover:text-[#4ABDBD] hover:bg-gray-50'
                                                } ${(isLoading || tabLocks[key]) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Tab Content */}
                <div data-testid="patient-profile-content" className="p-4 pb-0">
                    {isLoading ? (
                        <div data-testid="patient-profile-loading" className="flex flex-col items-center justify-center py-4 pt-12">
                            <CommonLoader />
                            <span data-testid="patient-profile-loading-text" className="text-xl text-[#4ABDBD]">{t('patientprofilepage.loading')}</span>
                        </div>
                    ) : (
                        <>
                            {activeTab === 'Profile' && (
                                <>
                                    {/* PROGRAMME (care programmes, Round B) — start /
                                        follow / close outside a visit too. The doctor;
                                        staff see the desk strip only while the patient
                                        has an open visit; the hospital admin nothing
                                        (owner, 28 Sep 2026 — the API refuses an admin's
                                        start / tick / close). Draws nothing when the
                                        hospital has no programmes. */}
                                    {(userRole === 'doctor' || userRole === 'user') && (selectedPatient?.patientId || patientId) && (
                                        <ProgrammeCard patientId={selectedPatient?.patientId || patientId} className="mt-4" />
                                    )}
                                    <PatientProfileTab patient={selectedPatient} id="patient-profile-tab-content-profile" />
                                </>
                            )}
                            {activeTab === 'Medical History' && (
                                <MedicalHistory medicalHistory={selectedPatient.medicalHistory} id="patient-profile-tab-content-medical-history" />
                            )}
                            {activeTab === 'Health Record' && visitSharingOn && (
                                <HealthRecordTab patientId={patientId} id="patient-profile-tab-content-health-record" />
                            )}
                            {activeTab === 'Dependencies' && (
                                <Dependencies id="patient-profile-tab-content-dependencies" />
                            )}
                            {activeTab === 'Custom Price' && customPricingOn && (
                                <CustomPrice customPricing={selectedPatient.customPricing} id="patient-profile-tab-content-custom-price" />
                            )}
                            {activeTab === 'Password' && (
                                <PatientPasswordTab patient={selectedPatient} id="patient-profile-tab-content-password" />
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default PatientProfile;